// Packages and components
import React, { useContext, useState, useEffect } from 'react';
import { Box } from 'grommet';
import { Checkmark, Close } from 'grommet-icons';
import { useModal } from '@components/ModalContext';
import CollectTeamInfo from './CollectTeamInfo';
import CompleteRoster from './CompleteRoster';
import CreateYourTeam from './CreateYourTeam';
import SHDBackButton from '@ui/BackButton/SHDBackButton';
import SHDContainer from '@ui/Container/SHDContainer';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDText from '@ui/Text/SHDText';
import SportsSelection from './SportsSelection';

// Helpers and utilities
import { analytics } from '@services/analytics';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@helpers/utils';
import { UserContext } from '@services/Session';
import UserStore from '@services/Session/store';
import segment, { SegmentEvents } from '@services/segment';

// Types and enums
import {
  ExternalTeamSearchResultOutput,
  ExternalSource as TeamImportExternalSource,
  TeamResponse,
  CreateTeamResponse,
} from '@dto/api';

import {
  OnboardingSteps,
  OnboardingStepsTracking,
  TeamImportStep,
  TeamImportPartner,
  SportType,
} from '@theme/theme.d';
import LiveStreamIntroPage from './LiveStream/LiveStreamSetUpIntroPage';
import { OnboardingPath } from '@services/segmentEvents';

export const renderModalContent = (
  isSuccess: boolean,
  heading: string,
  message: string
) => {
  const Icon = isSuccess ? Checkmark : Close;
  const iconColor = isSuccess ? 'black' : 'red';
  const borderColor = isSuccess ? 'black' : 'red';

  return (
    <SHDModalContainer>
      <Box
        pad={{ top: 'small', bottom: 'xxlarge', horizontal: 'xxxlarge' }}
        gap="xxxsmall"
        align="center"
        justify="center"
      >
        <Box
          round="full"
          pad="xxxsmall"
          border={{ color: borderColor, size: 'small' }}
          margin="none"
        >
          <Icon size="medium" color={iconColor} />
        </Box>
        <SHDHeading level="2">{heading}</SHDHeading>
        <SHDText style="subhead" textAlign="center">
          <p>{message}</p>
        </SHDText>
      </Box>
    </SHDModalContainer>
  );
};

const Onboarding: React.FC = () => {
  // States
  const [step, setStep] = useState<OnboardingSteps>(
    OnboardingSteps.SPORTS_SELECTION
  );
  const [importPartner, setImportPartner] =
    useState<TeamImportExternalSource | null>(null);
  const [teamSport, setTeamSport] = useState<SportType | null>(null);
  const [importTeam, setImportTeam] =
    useState<ExternalTeamSearchResultOutput | null>(null);

  // Hooks
  const { showModal, hideModal } = useModal();
  const userStore = useContext(UserContext);
  const { user } = useContext<UserStore>(UserContext);
  const query = useQuery();
  const fromExisting = !!query.get('from_existing');
  const history = useHistory();

  useEffect(() => {
    segment.track(SegmentEvents.ONBOARDING_STARTED, {
      type: OnboardingPath.TEAM_CREATION,
    });
  }, []);

  // Handlers
  const handleUserSuccessRedirect = (
    teamHandle: string,
    teamId: string,
    isAdmin: boolean,
    sportType: string
  ) => {
    history.push({
      pathname: `/${teamHandle}`,
      state: { teamId },
    });
    showModal(
      renderModalContent(
        true,
        'Congrats!',
        'Your team has been created. Schedule your first game to be live stream ready.'
      ),
      {
        onClose: () => {
          // Open the second modal after the first modal closes
          showModal(
            <LiveStreamIntroPage
              onClose={() => {
                hideModal();
                history.replace(`${window.location.pathname}?tab=`);
              }}
              history={history}
              teamId={teamId}
              isAdmin={isAdmin}
              teamName={teamHandle}
              sportType={sportType}
            />
          ),
            {
              className: 'is-transparent-controls',
            };
        },
        className: 'is-transparent-controls',
        onGoBack: undefined, // Prevent "Go Back" button from appearing
      }
    );
  };

  const handleStepChange = (
    newStep: OnboardingSteps,
    completedStep?: OnboardingStepsTracking,
    selectedSport?: SportType
  ) => {
    if (completedStep) {
      handleTrackingEvent('nextStep', completedStep);
      segment.track(SegmentEvents.ONBOARDING_STEP_COMPLETED, {
        step: completedStep,
        sport: teamSport || selectedSport,
      });
    }
    setStep(newStep);
  };

  const handleTrackingEvent = (
    type: 'nextStep' | 'goback',
    nextStep: OnboardingStepsTracking,
    currentStep?: OnboardingStepsTracking
  ) => {
    switch (type) {
      case 'nextStep':
        analytics.track('Onboarding Step Completed', {
          type: 'Team Onboarding',
          step: nextStep,
          sport: teamSport,
        });
        break;
      case 'goback':
        analytics.track('Onboarding Back Clicked', {
          type: 'Team Onboarding',
          currentStep: currentStep,
          previousStep: nextStep,
        });
        break;
    }
  };

  const handleGoBack = () => {
    switch (step) {
      case OnboardingSteps.CREATE_TEAM:
        handleTrackingEvent(
          'goback',
          OnboardingStepsTracking.SPORTS_SELECTION,
          OnboardingStepsTracking.CREATE_TEAM
        );
        handleStepChange(OnboardingSteps.SPORTS_SELECTION);
        break;

      case OnboardingSteps.COMPLETE_ROSTER:
        handleTrackingEvent(
          'goback',
          OnboardingStepsTracking.CREATE_TEAM,
          OnboardingStepsTracking.COMPLETE_ROSTER
        );
        handleStepChange(OnboardingSteps.CREATE_TEAM);
        break;

      case OnboardingSteps.ABOUT_TEAM:
        handleTrackingEvent(
          'goback',
          OnboardingStepsTracking.CREATE_TEAM,
          OnboardingStepsTracking.ABOUT_TEAM
        );
        handleStepChange(OnboardingSteps.CREATE_TEAM);
        break;

      case OnboardingSteps.SPORTS_SELECTION:
        handleTrackingEvent(
          'goback',
          OnboardingStepsTracking.EXIT_ONBOARDING,
          OnboardingStepsTracking.SPORTS_SELECTION
        );
        history.goBack();
        break;

      default:
        history.goBack(); // Fallback for unexpected steps
        break;
    }
  };

  const handleSportsSelectionContinue = (sport: SportType) => {
    setTeamSport(sport);
    handleStepChange(
      OnboardingSteps.CREATE_TEAM,
      OnboardingStepsTracking.SPORTS_SELECTION,
      sport
    );
  };

  const handleCreateYourTeamContinue = (
    teamStep: TeamImportStep,
    selectedImportTeam: ExternalTeamSearchResultOutput
  ) => {
    setImportPartner(teamStep as TeamImportExternalSource);
    setImportTeam(selectedImportTeam);
    handleStepChange(
      OnboardingSteps.COMPLETE_ROSTER,
      OnboardingStepsTracking.CREATE_TEAM
    );
  };

  const handleCreateTeam = (teamStatus: boolean, teamData?: TeamResponse) => {
    if (teamStatus && teamData) {
      handleTrackingEvent('nextStep', OnboardingStepsTracking.ABOUT_TEAM);
      analytics.track('Team Created', {
        userId: user._id,
        isFirstTeam: !fromExisting,
        sport: teamSport,
        teamId: teamData.id,
        importSource: importPartner,
      });
      segment.track(SegmentEvents.TEAM_CREATED, {
        teamName: teamData.nameLong,
        teamLevel: teamData.attrib_ageLevel,
        under13: teamData.isAge13Plus,
        sport: teamSport,
        source: 'manual',
      });
      const userIsAdmin = userStore.userTeams.some(
        (userTeam) => userTeam._id === teamData.id
      );
      if (teamData.nameHandle) {
        handleUserSuccessRedirect(
          teamData.nameHandle,
          teamData.id,
          userIsAdmin,
          teamData.attrib_sportType
        );
      }
    }
  };

  const handleCreateImportTeam = (
    teamStatus: boolean,
    teamData?: CreateTeamResponse
  ) => {
    if (teamStatus && teamData) {
      handleTrackingEvent('nextStep', OnboardingStepsTracking.COMPLETE_ROSTER);
      analytics.track('Team Created', {
        userId: user._id,
        isFirstTeam: !fromExisting,
        sport: teamSport,
        teamId: teamData.team._id,
        importSource: importPartner,
      });
      segment.track(SegmentEvents.ONBOARDING_STEP_COMPLETED, {
        step: OnboardingStepsTracking.COMPLETE_ROSTER,
        sport: teamSport,
      });
      segment.track(SegmentEvents.TEAM_CREATED, {
        teamName: teamData.team.nameLong,
        teamLevel: teamData.team.attrib_ageLevel,
        under13: teamData.team.isAge13Plus,
        sport: teamSport,
        source: 'import',
      });
      const userIsAdmin = userStore.userTeams.some(
        (userTeam) => userTeam._id === teamData.team._id
      );
      if (
        teamData.team?.nameHandle &&
        teamData.team._id &&
        teamData.team.attrib_sportType
      ) {
        handleUserSuccessRedirect(
          teamData.team.nameHandle,
          teamData.team._id,
          userIsAdmin,
          teamData.team.attrib_sportType
        );
      }
    }
  };

  // Methods
  const renderScreenByStep = () => {
    switch (step) {
      case OnboardingSteps.SPORTS_SELECTION:
        return (
          <SportsSelection
            userId={user._id}
            onContinue={handleSportsSelectionContinue}
          />
        );
      case OnboardingSteps.CREATE_TEAM:
        return (
          <CreateYourTeam
            importPartner={importPartner}
            sport={teamSport}
            onContinue={(teamStep, selectedImportTeam) => {
              if (teamStep === 'manual') {
                handleStepChange(
                  OnboardingSteps.ABOUT_TEAM,
                  OnboardingStepsTracking.CREATE_TEAM
                );
                return;
              }
              handleCreateYourTeamContinue(
                TeamImportExternalSource[teamStep as TeamImportPartner],
                selectedImportTeam as ExternalTeamSearchResultOutput
              );
            }}
          />
        );
      case OnboardingSteps.ABOUT_TEAM:
        return (
          <CollectTeamInfo
            sport={teamSport}
            onContinue={(teamStatus, teamData) =>
              handleCreateTeam(teamStatus, teamData)
            }
          />
        );

      case OnboardingSteps.COMPLETE_ROSTER:
        return (
          <CompleteRoster
            importPartner={importPartner}
            team={importTeam}
            onContinue={(teamStatus, teamData) =>
              handleCreateImportTeam(teamStatus, teamData)
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <SHDContainer maxWidth="small" pad="none">
      <Box
        style={{
          position: 'relative',
          top: '12px',
        }}
        alignSelf="start"
        pad={{ left: 'large' }}
        margin={{ bottom: 'xxxsmall' }}
      >
        <SHDBackButton onClick={handleGoBack} />
      </Box>
      {renderScreenByStep()}
    </SHDContainer>
  );
};

export default Onboarding;
